/**
 * Header component with logo, global search, and user menu.
 */

import { FC, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  AccountCircle,
  CloudUpload,
  Router as SwitchIcon,
  Lan as VlanIcon,
  Cable as InterfaceIcon,
  Logout,
  LockReset,
} from '@mui/icons-material';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/services/api';
import type { SearchResult } from '@/types/entities';

export const Header: FC = () => {
  const { toggleSidebar } = useUIStore();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // Debounced search function
  const handleSearchInput = useCallback(async (_event: React.SyntheticEvent, value: string) => {
    setSearchQuery(value);

    if (value.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }

    setSearchLoading(true);
    setSearchOpen(true); // Always open when typing 2+ characters

    try {
      const response = await api.search(value, undefined, 20);
      setSearchResults(response.results);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Handle selection
  const handleSearchSelect = (
    _event: React.SyntheticEvent,
    value: string | SearchResult | null
  ) => {
    if (!value || typeof value === 'string') return;

    // Navigate based on type
    if (value.type === 'switch') {
      navigate(`/switch/${value.id}`);
    } else if (value.type === 'vlan') {
      navigate(`/vlan/${value.id}`);
    } else if (value.type === 'interface') {
      navigate(`/interface/${value.id}`);
    }

    // Reset search
    setSearchQuery('');
    setSearchResults([]);
    setSearchOpen(false);
  };

  // Get icon for result type
  const getIcon = (type: string) => {
    switch (type) {
      case 'switch':
        return <SwitchIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />;
      case 'vlan':
        return <VlanIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />;
      case 'interface':
        return <InterfaceIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />;
      default:
        return null;
    }
  };

  // User menu handlers
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleChangePassword = () => {
    handleUserMenuClose();
    navigate('/change-password');
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
    navigate('/login');
  };

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          onClick={toggleSidebar}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 0, mr: 4 }}>
          NetGraph
        </Typography>

        <Box sx={{ flexGrow: 1, maxWidth: 600 }}>
          <Autocomplete
            freeSolo
            open={searchOpen}
            onOpen={() => {
              if (searchQuery.length >= 2) {
                setSearchOpen(true);
              }
            }}
            onClose={(_event, reason) => {
              // Only close on explicit actions (escape, select-option), not on blur
              if (reason === 'escape' || reason === 'selectOption') {
                setSearchOpen(false);
              }
              // Also close if query is too short
              if (searchQuery.length < 2) {
                setSearchOpen(false);
              }
            }}
            options={searchResults}
            filterOptions={(x) => x}
            loading={searchLoading}
            inputValue={searchQuery}
            onInputChange={handleSearchInput}
            onChange={handleSearchSelect}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
            groupBy={(option) =>
              typeof option === 'string' ? '' : option.type.toUpperCase()
            }
            renderOption={(props, option) => {
              if (typeof option === 'string') return null;

              const { key, ...otherProps } = props;
              return (
                <Box
                  component="li"
                  key={key}
                  {...otherProps}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  {getIcon(option.type)}
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    {option.subtitle && (
                      <Typography variant="caption" color="text.secondary">
                        {option.subtitle}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Search switches, VLANs, interfaces..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                  sx: {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    },
                    color: 'white',
                    '& .MuiAutocomplete-endAdornment': {
                      right: '9px !important',
                    },
                  },
                }}
              />
            )}
            noOptionsText={
              searchQuery.length < 2
                ? 'Type at least 2 characters to search'
                : 'No results found'
            }
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <IconButton color="inherit" sx={{ mr: 1 }} onClick={() => navigate('/transactions')}>
          <CloudUpload />
        </IconButton>

        <IconButton color="inherit" onClick={handleUserMenuOpen}>
          <AccountCircle />
        </IconButton>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user?.full_name || user?.username}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={handleChangePassword}>
            <ListItemIcon>
              <LockReset fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change Password</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};
